import { useEffect, useRef, useState } from 'react';

interface Props {
  value: number;
  onChange: (value: number) => void;
  options: readonly string[];
}

export function ProjectTypeSelect({ value, onChange, options }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const trigger = useRef<HTMLButtonElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  // Other remains available, after the five named types shown in Figma.
  const matches = options
    .map((label, index) => ({ label, index }))
    .filter(({ label }) =>
      label.toLowerCase().includes(search.trim().toLowerCase())
    )
    .sort((a, b) => Number(a.index === 0) - Number(b.index === 0));
  useEffect(() => {
    if (open) searchInput.current?.focus();
  }, [open]);
  function select(index: number) {
    onChange(index);
    setOpen(false);
    setSearch('');
    trigger.current?.focus();
  }
  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        id="projectType"
        ref={trigger}
        type="button"
        role="combobox"
        aria-labelledby="projectTypeLabel"
        aria-expanded={open}
        aria-controls="projectTypeChoices"
        aria-haspopup="listbox"
        className={`flex h-11 w-full items-center justify-between rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] px-4 text-left text-base font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--state-brand-active] ${open ? '!border-[3px] !border-[--state-brand-selected] !bg-[--base-surface-2]' : ''}`}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span
          className={
            value < 0
              ? 'text-[--text-dark-tertiary]'
              : 'text-[--text-dark-primary]'
          }
        >
          {options[value] ?? 'Audit, MVP, Frontend, Smart Contract, etc.'}
        </span>
        <i
          aria-hidden="true"
          className={`ri-arrow-${open ? 'up' : 'down'}-s-line shrink-0 text-2xl text-[--text-dark-secondary]`}
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-[6px] flex-col overflow-hidden rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] shadow-[--elevation-sm]">
          <div className="flex min-h-12 items-center gap-[10px] border-b border-[--base-border] px-4">
            <i
              aria-hidden="true"
              className="ri-search-line text-2xl text-[--text-dark-tertiary]"
            />
            <input
              ref={searchInput}
              aria-label="Search project types"
              placeholder="Search"
              value={search}
              className="!h-11 !min-w-0 !border-0 !bg-transparent !p-0 !text-base focus:!ring-0"
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (matches[0]) select(matches[0].index);
                }
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  list.current
                    ?.querySelector<HTMLButtonElement>('button')
                    ?.focus();
                }
              }}
            />
          </div>
          <div
            id="projectTypeChoices"
            ref={list}
            role="listbox"
            aria-labelledby="projectTypeLabel"
            className="!block max-h-96 overflow-y-auto"
            onKeyDown={(event) => {
              if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
              event.preventDefault();
              const buttons = Array.from(
                list.current?.querySelectorAll<HTMLButtonElement>('button') ??
                  []
              );
              const current = buttons.indexOf(
                document.activeElement as HTMLButtonElement
              );
              const next = current + (event.key === 'ArrowDown' ? 1 : -1);
              if (next < 0) searchInput.current?.focus();
              else buttons[Math.min(next, buttons.length - 1)]?.focus();
            }}
          >
            {matches.map(({ label, index }) => (
              <button
                key={index}
                type="button"
                role="option"
                aria-selected={value === index}
                className="flex min-h-12 w-full items-center px-4 text-left text-base font-medium text-[--text-dark-primary] hover:bg-[--base-surface-2] focus-visible:bg-[--base-surface-2] focus-visible:outline-none"
                onClick={() => select(index)}
              >
                {label}
              </button>
            ))}
            {!matches.length && (
              <p
                role="status"
                className="px-4 py-3 text-[--text-dark-secondary]"
              >
                No matching project types
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
